/**
 * Maps each Romania application-portal URL path to a stable New Relic step
 * name. Values are used in two places:
 *
 *  1. `trackAction('form_step_submitted', { step })` — the custom event name
 *     that appears in the NR dashboard under Browser → Custom Events.
 *
 *  2. `setCurrentRouteName(stepName)` inside NewRelicPageViewTracker so that
 *     NR groups page-view data by logical step rather than by raw URL (which
 *     changes per-environment when using hash routing or dynamic segments).
 *
 * Naming convention:  `<domain>:<step>` using kebab-case.
 * Domain values: form | verification | offer | documents | outcome | misc
 *
 * Keep in sync with apps/ro/src/business-logic/schemas/main-schema.ts.
 */
export const RO_STEP_NAMES: Readonly<Record<string, string>> = {
  // --- Form steps ---
  '/digital-offer': 'offer:provisional',
  '/person-details': 'form:contact-details',
  '/address-details': 'form:address',
  '/identity-data': 'form:identity',
  '/financial-data': 'form:financial',
  '/employment-data': 'form:employment',
  '/marital-status-details': 'form:marital-status',
  '/statements': 'form:statements',

  // --- Verification steps ---
  '/verification/consent/kyc': 'verification:kyc-consent',
  '/verification/holding-screen/kyc': 'verification:kyc-pending',
  '/verification/phone-validation': 'verification:phone',
  '/verification/email-validation': 'verification:email',
  '/verification/email-activation': 'verification:email-activation',
  '/verification/consent/fiscal': 'verification:fiscal-consent',
  '/verification/fiscal-sign': 'verification:fiscal-signing',
  '/verification/holding-screen/fiscal': 'verification:fiscal-pending',

  // --- Offer steps ---
  '/final-offer': 'offer:final',
  '/referral-offer': 'offer:referral',
  '/agreements': 'offer:agreements',

  // --- Document steps ---
  '/documents/precontract-document': 'documents:precontract',
  '/documents/consent/disbursement': 'documents:disbursement-consent',
  '/documents/holding-screen/disbursement': 'documents:disbursement-pending',
  '/documents/holding-screen/documents-signing': 'documents:signing-pending',

  // --- Outcomes ---
  '/thank-you': 'outcome:success',
  '/thank-you/referral': 'outcome:success:referral',
  '/thank-you/disbursement': 'outcome:success:disbursement',
  '/thank-you/card': 'outcome:success:card',
  '/declined': 'outcome:declined',
  '/technical-error': 'outcome:technical-error',
  '/dashboard': 'outcome:dashboard',

  // --- Misc ---
  '/small-calculator-page': 'misc:small-calculator',
  '/login': 'misc:login',
  '/register-otp': 'misc:register-otp',
  '/broken-journey': 'misc:broken-journey',
  '/loading-page': 'misc:loading',

  // --- Romanian localized public paths (from apps/ro/src/i18n/routing.ts) ---
  // The RO app uses next-intl with localized pathnames (localePrefix: 'never').
  // usePathname() returns the public Romanian URL, not the internal English key,
  // so we must map both forms to the same step name.
  '/credit-online/date-personale': 'form:contact-details',
  '/credit-online/adresa': 'form:address',
  '/credit-online/date-identificare': 'form:identity',
  '/credit-online/date-financiare': 'form:financial',
  '/credit-online/date-profesionale': 'form:employment',
  '/credit-online/date-suplimentare': 'form:marital-status',
  '/credit-online/declaratii': 'form:statements',
  '/credit-online/acorduri': 'offer:agreements',
  '/credit-online/oferta-initiala': 'offer:provisional',
  '/credit-online/oferta-finala': 'offer:final',
  '/credit-online/oferta-referral': 'offer:referral',
  '/credit-online/respins': 'outcome:declined',
  '/credit-online/verificare/kyc/acord': 'verification:kyc-consent',
  '/credit-online/verificare/kyc/asteptare': 'verification:kyc-pending',
  '/credit-online/verificare/validare-telefon': 'verification:phone',
  '/credit-online/verificare/validare-email': 'verification:email',
  '/credit-online/verificare/confirmare-email': 'verification:email-activation',
  '/credit-online/anaf/acord': 'verification:fiscal-consent',
  '/credit-online/verificare/anaf/document': 'verification:fiscal-signing',
  '/credit-online/verificare/anaf/asteptare': 'verification:fiscal-pending',
  '/credit-online/documente-precontractuale': 'documents:precontract',
  '/credit-online/documente/acord-virament': 'documents:disbursement-consent',
  '/credit-online/documente/asteptare-virament':
    'documents:disbursement-pending',
  '/credit-online/verificare/semnare-documente': 'documents:signing-pending',
  '/credit-online/felicitari': 'outcome:success',
  '/credit-online/felicitari/virament': 'outcome:success:disbursement',
  '/credit-online/felicitari/card': 'outcome:success:card',
  '/credit-online/felicitari/recomandare': 'outcome:success:referral',
  '/inregistrare-otp': 'misc:register-otp',
  '/contul-meu': 'outcome:dashboard',
  '/eroare-tehnica': 'outcome:technical-error',
  '/incarcare': 'misc:loading',
  '/cerere-intrerupta': 'misc:broken-journey',
} as const;

/**
 * Returns the New Relic step name for a given URL pathname, or a sanitised
 * fallback derived from the first path segment so we never send raw URLs.
 *
 * @param pathname - e.g. `/verification/phone-validation` (no query string)
 */
export function getStepName(pathname: string): string {
  if (RO_STEP_NAMES[pathname]) {
    return RO_STEP_NAMES[pathname];
  }
  // Dynamic segments or unmapped routes: use only the first path segment as a
  // best-effort label (avoids leaking ids/tokens into the step name).
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return 'misc:root';
  return `misc:${parts[0]}` || 'misc:unknown';
}
